import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import InputField from './form/InputField';
import PhotoUpload from './form/PhotoUpload';
import SafeIcon from '../common/SafeIcon';

export default function RegistrationForm({ t, onBack, onSubmit, isSubmitting }) {
  const [formData, setFormData] = useState({
    checkIn: '',
    checkOut: '',
    checkInTime: '',
    whatsapp: '',
    guests: 1,
    reminders: true,
    guest1: { fullName: '', nationality: '', idNumber: '', photoFile: null },
    guest2: { fullName: '', nationality: '', idNumber: '', photoFile: null },
    guest3: { fullName: '', nationality: '', idNumber: '', photoFile: null }
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleGuestChange = (guestKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      [guestKey]: { ...prev[guestKey], [field]: value }
    }));
  };

  const setGuestCount = (count) => {
    setFormData(prev => ({ ...prev, guests: count }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6 pb-20"
    >
      <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-emerald-800 font-medium transition-colors">
        <SafeIcon name="ArrowLeft" /> Volver
      </button>

      <form className="space-y-8" onSubmit={handleSubmit}>
        {/* Guest Count Selector */}
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
          <label className="block text-sm font-semibold text-gray-700 mb-4 text-center uppercase tracking-wider">
            {t.guests}
          </label>
          <div className="flex justify-center gap-4">
            {[1, 2, 3].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setGuestCount(num)}
                className={`w-14 h-14 rounded-2xl font-bold text-lg transition-all flex items-center justify-center border-2 ${
                  formData.guests === num
                    ? 'bg-emerald-800 border-emerald-800 text-white shadow-lg shadow-emerald-900/20'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-emerald-200'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        {/* Guest Cards */}
        <div className="space-y-6">
          <AnimatePresence mode="popLayout">
            {[...Array(formData.guests)].map((_, index) => {
              const guestNum = index + 1;
              const guestKey = `guest${guestNum}`;
              const guest = formData[guestKey];

              return (
                <motion.div
                  key={guestKey}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-white p-6 md:p-8 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-2 h-full bg-emerald-800" />
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="bg-emerald-100 text-emerald-800 w-8 h-8 rounded-lg flex items-center justify-center text-sm">
                      {guestNum}
                    </span>
                    {guestNum === 1 ? t.fullName : `${t.fullName} #${guestNum}`}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField 
                      label={t.fullName}
                      value={guest.fullName}
                      onChange={(e) => handleGuestChange(guestKey, 'fullName', e.target.value)}
                      placeholder={t.fullNamePlaceholder}
                      required
                    />
                    <InputField 
                      label={t.nationality}
                      value={guest.nationality}
                      onChange={(e) => handleGuestChange(guestKey, 'nationality', e.target.value)}
                      placeholder={t.nationalityPlaceholder}
                      required
                    />
                    <InputField 
                      label={t.idNumber}
                      value={guest.idNumber}
                      onChange={(e) => handleGuestChange(guestKey, 'idNumber', e.target.value)}
                      placeholder={t.idNumberPlaceholder}
                      required
                    />
                    <PhotoUpload 
                      label={t.idPhoto}
                      helpText={t.idPhotoHelp}
                      t={t}
                      onChange={(file) => handleGuestChange(guestKey, 'photoFile', file)}
                    />
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Common Fields (Stay & Contact) */}
        <div className="bg-white p-6 md:p-8 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <SafeIcon name="Calendar" className="text-emerald-800" />
            Stay & Contact Info
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField 
              label={t.checkIn} name="checkIn" type="date" value={formData.checkIn}
              onChange={handleChange} required min={new Date().toISOString().split('T')[0]}
            />
            <InputField 
              label={t.checkOut} name="checkOut" type="date" value={formData.checkOut}
              onChange={handleChange} required min={formData.checkIn || new Date().toISOString().split('T')[0]}
            />
            <div className="md:col-span-2">
              <InputField 
                label={t.checkInTime} name="checkInTime" type="time" value={formData.checkInTime}
                onChange={handleChange} required
              />
            </div>
            <div className="md:col-span-2">
              <InputField 
                label={t.whatsapp} name="whatsapp" type="tel" value={formData.whatsapp}
                onChange={handleChange} placeholder="+57 300 000 0000" helpText={t.whatsappHelp} required
              />
            </div>
          </div>

          {/* Reminders Toggle */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="relative flex items-center justify-center h-6 w-6 mt-1 flex-shrink-0">
                <input
                  type="checkbox"
                  name="reminders"
                  checked={formData.reminders}
                  onChange={handleChange}
                  className="peer absolute h-full w-full opacity-0 cursor-pointer"
                />
                <div className="h-6 w-6 rounded-lg border-2 border-gray-300 bg-white transition-all peer-checked:bg-emerald-800 peer-checked:border-emerald-800 group-hover:border-emerald-400 flex items-center justify-center">
                  <SafeIcon name="Check" className="text-white text-sm scale-0 transition-transform peer-checked:scale-100" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-gray-900 group-hover:text-emerald-900 transition-colors">
                  {t.reminders}
                </span>
                <span className="text-sm text-gray-500 leading-snug">
                  {t.remindersHelp}
                </span>
              </div>
            </label>
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-emerald-800 hover:bg-emerald-900 text-white font-bold py-5 px-6 rounded-2xl transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed shadow-xl shadow-emerald-900/30 text-lg"
          >
            {isSubmitting ? (
              <>
                <SafeIcon name="Loader" className="animate-spin" />
                {t.submitting}
              </>
            ) : (
              <>
                {t.submit}
                <SafeIcon name="ArrowRight" />
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
}